/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { type Registry as Services, service } from '@ember/service';
import type { ModelRegistry } from 'ember-data/types/registries/model';

interface IArgs {
  booking: ModelRegistry['booking'];
}

export interface BookingSignature {
  Element: HTMLDivElement;

  Args: IArgs;

  Blocks: {
    default: [];
  };
}

export default class UiBookingComponent extends Component<BookingSignature> {
  @service router!: Services['router'];
  @service session!: Services['session'];

  @cached
  get booking(): ModelRegistry['booking'] {
    return this.args.booking;
  }

  @cached
  get currentUser(): ModelRegistry['user'] {
    return this.session.currentUser as ModelRegistry['user'];
  }

  @cached
  get currentRouteName(): string {
    return this.router.currentRouteName ?? '';
  }

  @cached
  get canEditPrice(): boolean {
    return (
      this.currentUser.hasAccessTo('bookings.price') &&
      this.booking.isPriceEditable
    );
  }

  @cached
  get canMessageGuest(): boolean {
    return this.currentUser.hasAccessTo('bookings.messages');
  }

  @cached
  get canSeeGuestIdentity(): boolean {
    return !!(
      this.currentUser.hasAccessTo('bookings.identity') &&
      this.booking.guestIdentityVerification?.succeeded
    );
  }

  @cached
  get isViewingReplies(): boolean {
    return this.currentRouteName.includes('bookings.replies');
  }

  @cached
  get isActionBookings(): boolean {
    return this.currentRouteName.startsWith('action.bookings');
  }

  @cached
  get isInboxBookings(): boolean {
    return this.currentRouteName.startsWith('xcalendar.inbox.bookings');
  }

  @cached
  get summaryRoute(): string {
    if (this.isActionBookings) {
      return 'action.bookings.summary.index';
    } else if (this.isInboxBookings) {
      return 'xcalendar.inbox.bookings.summary.index';
    }

    return 'xcalendar.bookings.summary';
  }

  @cached
  get priceRoute(): string {
    if (this.isActionBookings) {
      return 'action.bookings.price';
    } else if (this.isInboxBookings) {
      return 'xcalendar.inbox.bookings.price';
    }

    return 'xcalendar.bookings.price';
  }

  @cached
  get messagesRoute(): string {
    if (this.isActionBookings) {
      return 'action.bookings.messages';
    } else if (this.isInboxBookings) {
      return 'xcalendar.inbox.bookings.messages';
    }

    return 'xcalendar.bookings.messages';
  }

  @cached
  get internalMessagesRoute(): string {
    if (this.isActionBookings) {
      return 'action.bookings.internal-messages';
    } else if (this.isInboxBookings) {
      return 'xcalendar.inbox.bookings.internal-messages';
    }

    return 'xcalendar.bookings.internal-messages';
  }

  @cached
  get detailsRoute(): string {
    if (this.isActionBookings) {
      return 'action.bookings.details';
    } else if (this.isInboxBookings) {
      return 'xcalendar.inbox.bookings.details';
    }

    return 'xcalendar.bookings.details';
  }

  @cached
  get identityRoute(): string {
    if (this.isActionBookings) {
      return 'action.bookings.identity';
    } else if (this.isInboxBookings) {
      return 'xcalendar.inbox.bookings.identity';
    }

    return 'xcalendar.bookings.identity';
  }

  @cached
  get logsRoute(): string {
    if (this.isActionBookings) {
      return 'action.bookings.logs';
    } else if (this.isInboxBookings) {
      return 'xcalendar.inbox.bookings.logs';
    }

    return 'xcalendar.bookings.logs';
  }

  @cached
  get aiRoute(): string {
    if (this.isActionBookings) {
      return 'action.bookings.ai';
    } else if (this.isInboxBookings) {
      return 'xcalendar.inbox.bookings.ai';
    }

    return 'xcalendar.bookings.ai';
  }

  @cached
  get indexRoute(): string {
    if (this.currentRouteName.startsWith('action')) {
      return 'action';
    } else if (this.currentRouteName.startsWith('xcalendar.inbox')) {
      return 'xcalendar.inbox';
    }

    return 'xcalendar';
  }

  @cached
  get isModifyingBooking(): boolean {
    return (
      this.currentRouteName.includes('bookings.summary.modify') ||
      this.currentRouteName.includes('bookings.summary.accept')
    );
  }

  @cached
  get hideNavPills(): boolean {
    return this.isViewingReplies || this.isModifyingBooking;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking': typeof UiBookingComponent;
  }
}
